// src/components/AbastecimentoManualModal.jsx
//
// Modal GLOBAL de lançamento manual de abastecimento para UM veículo.
// Extraído da aba Logs do VeiculoEditModal e promovido a modal próprio,
// acessível pelo menu "Ações" da página de Frota. Diferença em relação ao
// embutido: aqui o veículo é escolhido por um seletor de placa (o id_alvo
// monta a URL POST /api/vehicles/:id/abastecimentos).
//
// Reaproveita o backend existente:
//   - GET  /api/vehicles/:id/ultimo-km          → pré-preenche o odômetro
//   - POST /api/vehicles/:id/abastecimentos     → grava o lançamento
//
// CSS: reusa VeiculoEditModal.css (classes vec-fuel-*, vec-form*, vec-btn-fuel,
// vec-edit-placa-plate) + o shell vec-modal-* já global via Veiculos.css.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaGasPump, FaPlus, FaSpinner, FaTimes, FaCalculator, FaRoute,
  FaChartLine, FaExclamationTriangle, FaCar,
} from 'react-icons/fa';
import './VeiculoEditModal.css';

const TIPOS_COMBUSTIVEL = ['Gasolina', 'Etanol', 'Diesel', 'Diesel S-10', 'GNV'];

const formatCurrency = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const formatDecimalBR = (val, casas = 2) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

const formatKm = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n.toLocaleString('pt-BR')} km`;
};

const formatKmL = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${formatDecimalBR(n, 2)} km/L`;
};

const getNowLocalDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const normalizeDriverId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id).replace('MOT-', '').replace('USR-', '').trim();
};

const EMPTY_FORM = {
  data_abastecimento: getNowLocalDate(),
  litros: '',
  valor: '',
  odometro_registrado: '',
  motorista_id: '',
  tipo_combustivel: '',
  posto: '',
  observacao: '',
};

export default function AbastecimentoManualModal({
  veiculos = [],
  motoristas = [],
  preSelectedId = '',
  onClose,
  onSuccess,
}) {
  const [veiculoId, setVeiculoId] = useState(preSelectedId ? String(preSelectedId) : '');
  const [form, setForm] = useState(EMPTY_FORM);
  const [ultimoKm, setUltimoKm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Trava o pré-preenchimento do odômetro a UMA vez por veículo, para não
  // sobrescrever o que o usuário já digitou em refetches.
  const odoPreenchidoRef = useRef(false);

  const motoristasNormalizados = useMemo(
    () =>
      (motoristas || []).map((m) => ({
        ...m,
        id_normalizado: normalizeDriverId(m.id),
        label_extra:
          m.origem === 'operacional' ? ' (Operacional)'
          : m.origem === 'motorista' ? ' (Motorista)'
          : '',
        optionKey: m.id_raw || m.id,
      })),
    [motoristas]
  );

  const veiculoSelecionado = useMemo(
    () => veiculos.find((v) => String(v.id) === String(veiculoId)) || null,
    [veiculos, veiculoId]
  );

  const fetchUltimoKm = useCallback(async (id) => {
    if (!id) { setUltimoKm(null); return; }
    try {
      const { data } = await api.get(`/api/vehicles/${id}/ultimo-km`);
      if (data?.ultimo_km > 0) {
        setUltimoKm(data.ultimo_km);
        if (!odoPreenchidoRef.current) {
          setForm((prev) => ({ ...prev, odometro_registrado: String(data.ultimo_km) }));
          odoPreenchidoRef.current = true;
        }
      } else {
        setUltimoKm(null);
      }
    } catch {
      setUltimoKm(null);
    }
  }, []);

  // Ao trocar de veículo: zera o form (preservando data) e rebusca o último km.
  useEffect(() => {
    odoPreenchidoRef.current = false;
    setUltimoKm(null);
    setForm((prev) => ({ ...EMPTY_FORM, data_abastecimento: prev.data_abastecimento }));
    if (veiculoId) fetchUltimoKm(veiculoId);
  }, [veiculoId, fetchUltimoKm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Métricas ao vivo — espelham o cálculo do backend (R$/L, distância, km/L).
  const draft = useMemo(() => {
    const litros = parseFloat(form.litros);
    const valor = parseFloat(form.valor);
    const odo = Number.parseInt(form.odometro_registrado, 10);
    const ultimo = Number(ultimoKm) || 0;

    const valorPorLitro =
      Number.isFinite(litros) && litros > 0 && Number.isFinite(valor) && valor > 0
        ? valor / litros : null;

    const distancia =
      Number.isFinite(odo) && odo > 0 && ultimo > 0 && odo > ultimo ? odo - ultimo : null;

    const eficiencia =
      distancia && Number.isFinite(litros) && litros > 0 ? distancia / litros : null;

    const odometroMenorQueUltimo =
      Number.isFinite(odo) && odo > 0 && ultimo > 0 && odo < ultimo;

    return { valorPorLitro, distancia, eficiencia, odometroMenorQueUltimo };
  }, [form.litros, form.valor, form.odometro_registrado, ultimoKm]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!veiculoId) {
      toast.warning('Selecione um veículo antes de lançar.');
      return;
    }
    const litrosNum = parseFloat(form.litros);
    const valorNum = parseFloat(form.valor);
    if (!Number.isFinite(litrosNum) || litrosNum <= 0) {
      toast.warning('Informe a quantidade de litros.');
      return;
    }
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      toast.warning('Informe um valor válido.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/api/vehicles/${veiculoId}/abastecimentos`, {
        data_abastecimento: form.data_abastecimento,
        litros: litrosNum,
        valor: valorNum,
        odometro_registrado: form.odometro_registrado || null,
        motorista_id: form.motorista_id ? Number(normalizeDriverId(form.motorista_id)) : null,
        tipo_combustivel: form.tipo_combustivel || null,
        posto: form.posto || null,
        observacao: form.observacao || null,
      });
      const placa = veiculoSelecionado?.placa ? ` (${veiculoSelecionado.placa})` : '';
      toast.success(`Abastecimento lançado${placa}!`);
      // Mantém o modal aberto para lançamentos em sequência: limpa o form,
      // preserva o veículo e atualiza o último km registrado.
      odoPreenchidoRef.current = false;
      setForm((prev) => ({ ...EMPTY_FORM, data_abastecimento: prev.data_abastecimento }));
      await fetchUltimoKm(veiculoId);
      onSuccess?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Falha ao registrar abastecimento.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vec-modal-overlay" onClick={onClose}>
      <div className="vec-modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="vec-modal-header">
          <h2><FaGasPump /> Lançar Abastecimento</h2>
          <button className="vec-close" onClick={onClose}><FaTimes /></button>
        </header>

        <div className="vec-modal-body">
          {/* Seletor de veículo */}
          <div className="vec-form-group vec-form">
            <label>Veículo</label>
            <select value={veiculoId} onChange={(e) => setVeiculoId(e.target.value)} required>
              <option value="">Selecione o veículo...</option>
              {veiculos.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.placa} — {v.marca} {v.modelo}
                </option>
              ))}
            </select>
          </div>

          {!veiculoId ? (
            <div className="vec-empty-box" style={{ marginTop: 14 }}>
              <FaCar style={{ marginRight: 6 }} />
              Escolha um veículo acima para lançar o abastecimento.
            </div>
          ) : (
            <>
              {/* Placa do veículo escolhido */}
              {veiculoSelecionado?.placa && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0 14px' }}>
                  <span className="vec-edit-placa-plate">
                    <span className="vec-edit-placa-plate__bar">BRASIL</span>
                    <span className="vec-edit-placa-plate__num">{veiculoSelecionado.placa}</span>
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="vec-form vec-form-compact">
                <div className="vec-form-compact-row">
                  <div className="vec-input-group">
                    <label>Data</label>
                    <input
                      type="date" name="data_abastecimento"
                      value={form.data_abastecimento} onChange={handleChange} required
                    />
                  </div>
                  <div className="vec-input-group">
                    <label>Litros</label>
                    <input
                      type="number" step="0.01" name="litros"
                      value={form.litros} onChange={handleChange} required placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="vec-form-compact-row">
                  <div className="vec-input-group">
                    <label>Valor total (R$)</label>
                    <input
                      type="number" step="0.01" name="valor"
                      value={form.valor} onChange={handleChange} required
                      className="vec-input-money" placeholder="0.00"
                    />
                  </div>
                  <div className="vec-input-group">
                    <label>Odômetro atual (km)</label>
                    <input
                      type="number" name="odometro_registrado"
                      value={form.odometro_registrado} onChange={handleChange}
                      placeholder="Ex.: 158420"
                    />
                    {ultimoKm > 0 && (
                      <span className="vec-km-hint">
                        📍 Último registrado: <strong>{ultimoKm.toLocaleString('pt-BR')} km</strong>
                      </span>
                    )}
                  </div>
                </div>

                {/* Métricas ao vivo */}
                <div className="vec-fuel-live-metrics">
                  <div className="vec-fuel-live-card">
                    <span className="vec-fuel-live-icon"><FaCalculator /></span>
                    <div>
                      <label>Valor por litro</label>
                      <strong>{draft.valorPorLitro ? `${formatCurrency(draft.valorPorLitro)} / L` : '—'}</strong>
                    </div>
                  </div>
                  <div className="vec-fuel-live-card">
                    <span className="vec-fuel-live-icon"><FaRoute /></span>
                    <div>
                      <label>Distância do último abastecimento</label>
                      <strong>{draft.distancia ? formatKm(draft.distancia) : '—'}</strong>
                    </div>
                  </div>
                  <div className="vec-fuel-live-card">
                    <span className="vec-fuel-live-icon"><FaChartLine /></span>
                    <div>
                      <label>Eficiência estimada</label>
                      <strong>{draft.eficiencia ? formatKmL(draft.eficiencia) : '—'}</strong>
                    </div>
                  </div>
                  {draft.odometroMenorQueUltimo && (
                    <div className="vec-fuel-live-warning">
                      <FaExclamationTriangle />O odômetro informado está menor que o último registrado.
                    </div>
                  )}
                </div>

                <div className="vec-input-group">
                  <label>Combustível (Opcional)</label>
                  <select name="tipo_combustivel" value={form.tipo_combustivel} onChange={handleChange}>
                    <option value="">Não informado</option>
                    {TIPOS_COMBUSTIVEL.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="vec-input-group">
                  <label>Motorista (Opcional)</label>
                  <select name="motorista_id" value={form.motorista_id} onChange={handleChange}>
                    <option value="">Não informado</option>
                    {motoristasNormalizados.map((m) => (
                      <option key={m.optionKey} value={m.id_normalizado}>{m.nome}{m.label_extra}</option>
                    ))}
                  </select>
                </div>
                <div className="vec-input-group">
                  <label>Posto (Opcional)</label>
                  <input
                    type="text" name="posto" value={form.posto} onChange={handleChange}
                    placeholder="Ex.: Posto BR — Rod. SP-330"
                  />
                </div>
                <div className="vec-input-group">
                  <label>Observação (Opcional)</label>
                  <input
                    type="text" name="observacao" value={form.observacao} onChange={handleChange}
                    placeholder="Detalhes..."
                  />
                </div>

                <button type="submit" className="vec-btn-fuel full-width" disabled={isSubmitting}>
                  {isSubmitting
                    ? <><FaSpinner className="fa-spin" /> Registrando...</>
                    : <><FaPlus /> Lançar Abastecimento</>}
                </button>
              </form>
            </>
          )}
        </div>

        <footer className="vec-modal-footer">
          <button type="button" className="vec-btn vec-btn-outline" onClick={onClose} disabled={isSubmitting}>
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}